import { useEffect, useState } from 'react';
import type { EmployeeDetailDto, EmployeeDto, PagedResult } from '@shared/types';
import { initials } from '@shared/format';
import { errorMessage, useApp, useLoad } from '../context';
import { navigate } from '../router';
import { Card, Empty, ErrorBox, Loading, TopBar } from '../components/ui';

/** Reasons an admin can pick when they see someone doing something right. Stored as the ledger description. */
export const OBSERVATION_REASONS = [
  'Safe work practice',
  'Good catch / hazard reported',
  'Stopped unsafe work',
  'Proper PPE',
  'Helped a coworker',
  'Housekeeping',
  'Other',
] as const;
/** Quick picks; the stepper covers every 0.25 step up to 5. */
const AMOUNTS = [0.25, 0.5, 1, 1.5, 2, 3, 5];
const MAX_POINTS = 5;
const STEP = 0.25;

type Picked = Pick<EmployeeDto, 'employeeId' | 'displayName' | 'jobTitle' | 'department'>;

/** Manual "observed doing something good" award. Posts a positive ManualAward ledger row (audited, never edited). */
export function Award({ employeeId }: { employeeId?: string }) {
  const { api, toast } = useApp();
  const [picked, setPicked] = useState<Picked | null>(null);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState<string>(OBSERVATION_REASONS[0]);
  const [amount, setAmount] = useState('1');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | { name: string; tokens: number }>(null);

  // Pre-select when opened from an employee profile.
  useEffect(() => {
    if (!employeeId) return;
    api.get<EmployeeDetailDto>(`/api/employees/${employeeId}`).then((d) => setPicked(d.employee)).catch(() => undefined);
  }, [api, employeeId]);

  const list = useLoad(
    () => picked || search.trim().length < 1 ? Promise.resolve(null)
      : api.get<PagedResult<EmployeeDto>>(`/api/employees?take=25&search=${encodeURIComponent(search.trim())}`),
    [api, search, picked]);

  const tokens = Number(amount);
  const description = [`Observed: ${reason}`, note.trim()].filter(Boolean).join(' — ');
  const valid = !!picked && Number.isInteger(tokens * 4) && tokens > 0 && tokens <= MAX_POINTS
    && (reason !== 'Other' || note.trim().length >= 3);

  const submit = async () => {
    if (!picked || !valid) return;
    setBusy(true);
    try {
      await api.post('/api/tokens/adjustments', { employeeId: picked.employeeId, tokenAmount: tokens, reason: description, kind: 'manual' });
      toast(`${tokens} point${tokens === 1 ? '' : 's'} awarded to ${picked.displayName}`);
      setDone({ name: picked.displayName, tokens });
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const again = () => { setDone(null); setPicked(null); setSearch(''); setNote(''); setAmount('1'); setReason(OBSERVATION_REASONS[0]); };

  if (done) return (
    <div className="page">
      <TopBar title="Award Points" back="/" />
      <Card>
        <div className="award-done">
          <div className="award-done-num tabular">+{done.tokens}</div>
          <div><strong>{done.name}</strong></div>
          <div className="muted small">{description}</div>
          <div className="muted small">It's on the shop leaderboard now and in the audit log.</div>
        </div>
        <button className="btn btn-primary btn-block mt" onClick={again}>Award someone else</button>
        <button className="btn btn-secondary btn-block mt" onClick={() => navigate('/')}>Done</button>
      </Card>
    </div>
  );

  return (
    <div className="page">
      <TopBar title="Award Points" back={employeeId ? `/employees/${employeeId}` : '/'} />
      <p className="muted small">Saw someone doing something right? Give them points on the spot.</p>

      <Card>
        <div className="card-kicker">1. Employee</div>
        {picked ? (
          <div className="list-row" style={{ cursor: 'default' }}>
            <span className="avatar-sm">{initials(picked.displayName)}</span>
            <div className="grow"><strong>{picked.displayName}</strong>
              <div className="muted small">{[picked.jobTitle, picked.department].filter(Boolean).join(' • ')}</div></div>
            <button className="btn-link small" onClick={() => { setPicked(null); setSearch(''); }}>Change</button>
          </div>
        ) : (
          <>
            <div className="search-wrap mt"><input className="input" type="search" autoFocus placeholder="Start typing a name…" value={search}
              onChange={(e) => setSearch(e.target.value)} aria-label="Search employees" /></div>
            {list.loading && search && <Loading label="Searching…" />}
            {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}
            {list.data && (list.data.items.length === 0 ? <Empty>No active employee matches “{search}”.</Empty> : list.data.items.map((e) => (
              <button key={e.employeeId} className="list-row" onClick={() => setPicked(e)}>
                <span className="avatar-sm">{initials(e.displayName)}</span>
                <div className="grow"><strong>{e.displayName}</strong>
                  <div className="muted small">{[e.jobTitle, e.department].filter(Boolean).join(' • ')}</div></div>
                <span className="chev">›</span>
              </button>
            )))}
          </>
        )}
      </Card>

      <Card>
        <div className="card-kicker">2. What did you see?</div>
        <div className="chips mt" role="radiogroup" aria-label="Reason">
          {OBSERVATION_REASONS.map((r) => (
            <button key={r} role="radio" aria-checked={reason === r} className={`chip ${reason === r ? 'on' : ''}`} onClick={() => setReason(r)}>{r}</button>
          ))}
        </div>
        <label className="field mt"><span>Note {reason === 'Other' ? '(required)' : '(optional)'}</span>
          <textarea className="input" rows={2} maxLength={300} placeholder="e.g. Tagged out the compressor before working on it"
            value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </Card>

      <Card>
        <div className="card-kicker">3. Points</div>
        <div className="chips mt" role="radiogroup" aria-label="Points">
          {AMOUNTS.map((a) => (
            <button key={a} role="radio" aria-checked={tokens === a} className={`chip chip-num ${tokens === a ? 'on' : ''}`} onClick={() => setAmount(String(a))}>{a}</button>
          ))}
        </div>
        <div className="pts-stepper" aria-label="Adjust points by 0.25">
          <button aria-label="Less" disabled={!(tokens > STEP)} onClick={() => setAmount(String(Math.max(STEP, Math.round((tokens - STEP) * 4) / 4)))}>−</button>
          <div className="pts-value" aria-live="polite">{Number.isFinite(tokens) ? tokens.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}<small>0.25 to {MAX_POINTS} points</small></div>
          <button aria-label="More" disabled={!(tokens < MAX_POINTS)} onClick={() => setAmount(String(Math.min(MAX_POINTS, Math.round((tokens + STEP) * 4) / 4)))}>+</button>
        </div>
      </Card>

      <button className="btn btn-go btn-block" disabled={!valid || busy} onClick={submit}>
        {busy ? 'Saving…' : picked ? `Award ${Number.isInteger(tokens * 4) && tokens > 0 ? tokens : ''} point${tokens === 1 ? '' : 's'} to ${picked.displayName.split(' ')[0]}` : 'Pick an employee first'}
      </button>
    </div>
  );
}
